import fetchData from "@/lib/Fetchers/fetchData";
import { GuestPicturesResponse } from "@/lib/Types/GuestPicturesResponse";
import { useState, useEffect, useCallback } from "react";
import { sendKibanaLog } from "./sendKibanaLog";
import { AppConfig } from "@/lib/Types/AppConfig";

interface GuestPicturesParams {
  cid: string;
  p: number;
  m: number;
}

interface UseGuestPicturesParams {
  hotelCID: string;
  appConfig: AppConfig;
}

const useGuestPictures = ({ hotelCID, appConfig }: UseGuestPicturesParams) => {
  const { envConfig, viewMode, searchParams } = appConfig;
  const [data, setData] = useState<GuestPicturesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGuestPicturesData = useCallback(async () => {
    if (!envConfig || !viewMode || !hotelCID) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const paramsObject: GuestPicturesParams = {
      cid: hotelCID,
      p: viewMode.profile,
      m: viewMode.mandator,
    };

    sendKibanaLog({
      envConfig,
      message: "GUEST PICTURES START",
      component: "guestPictures",
      jsonMessage: paramsObject,
      searchId: searchParams.searchId,
      category: "debug",
    });

    const baseUrl = "comvel-review-service/rest/hotel/review/guestPictures";
    const url = `${envConfig.api}/${baseUrl}`;

    const data = await fetchData({
      url,
      method: "POST",
      params: paramsObject,
      envConfig,
      service: "guestPictures",
      searchId: searchParams.searchId,
    });

    const isError = data.apiError;

    if (isError) {
      sendKibanaLog({
        envConfig,
        message: "GUEST PICTURES FAIL",
        component: "guestPictures",
        jsonMessage: data.message,
        searchId: searchParams.searchId,
        category: "debug",
      });
      setData({
        cid: parseInt(hotelCID),
        guestPictures: [],
        resultCount: 0,
        startPosition: 0,
        totalGuestPictureCount: 0,
      });
    } else {
      sendKibanaLog({
        envConfig,
        message: "GUEST PICTURES SUCCESS",
        component: "guestPictures",
        jsonMessage: {},
        searchId: searchParams.searchId,
        category: "debug",
      });
      setData(data);
    }

    setIsLoading(false);
  }, [envConfig, viewMode, hotelCID, searchParams]);

  useEffect(() => {
    fetchGuestPicturesData();
  }, [fetchGuestPicturesData]);

  const mutate = useCallback(() => {
    fetchGuestPicturesData();
  }, [fetchGuestPicturesData]);

  return {
    data,
    isLoading,
    mutate,
  };
};

export default useGuestPictures;
